import { Vehicle } from "../../../types/appTypes";
import { ModelCatalogFilterForm, ModelMatch, SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";


export type SubmitPayload = { selectedMatch: ModelMatch, appliedFilters?: ModelCatalogFilterForm };
export type ModalConfig = {
  inputVehicle: null | ModelMatcherInputVehicle;
  selectedMotorAtlasId: string | null | undefined
  presetFilters?: ModelCatalogFilterForm;
  views?: {
    hideSourceVehicle?: boolean;
  }
}

export type ModelMatcherInputVehicle = Pick<Vehicle,
  | "vin"
  | "link"
  | "firstRegDate"
  | "mileage"
  | "motorAtlasId"
  | "typeId"
>

export const hierarchy = ["type", "make", "model", "subModel", "variant"] as const;
export type HierarcyKey = typeof hierarchy[number];
export const isHierarchyKey = (val: unknown): val is HierarcyKey => hierarchy.includes(val as HierarcyKey);

export function clearDependentFields<
  T extends Record<string, unknown>,
  CV = undefined
>(
  obj: T,
  changedKey?: keyof T,
  clearValue: CV = undefined as CV
): T {
  if (!changedKey) return obj;

  if (!isHierarchyKey(changedKey)) {
    return obj; // nothing to clear
  }

  const idx = hierarchy.indexOf(changedKey);
  const toClear = hierarchy.slice(idx + 1);

  const result = { ...obj } as T & Partial<Record<HierarcyKey, CV>>;

  for (const key of toClear) {
    if (key in result) {
      result[key] = clearValue;
    }
  }

  return result as T;
}

export type BrowserOption = { label: string; value: string };
export type ModelBrowserOptions = {
  type: BrowserOption[],
  make: BrowserOption[],
  model: BrowserOption[],
  subModel: BrowserOption[],
  variant?: BrowserOption[]
}


export const getNextKey = (k: HierarcyKey): HierarcyKey | null => {
  const i = hierarchy.indexOf(k);
  return i >= 0 && i < hierarchy.length - 1 ? hierarchy[i + 1] : null;
};

export const transformForAutoComplete = (opts: BrowserOption[]): BrowserOption[] => {
  return opts.map(o => ({ label: o.label, value: o.label, key: o.value })); // UI shows label, value==label
}


export function mapRecommendedFiltersToForm(filters: SourceVehiclePreview['recommendedFilters'], potentialEcodes: string[]) {
  const res: ModelCatalogFilterForm = {
    potentialEcodesEnabled: potentialEcodes?.length > 1,
    bodyType: filters?.bodyType ?? [],
    driveType: filters?.driveType ?? [],
    fuelType: filters?.fuelType ?? [],
    gearbox: filters?.gearbox ?? [],
    make: filters?.make,
    model: filters?.model,
    subModel: filters?.subModel,
    type: filters?.type ?? "01",
    year: {
      from: filters?.yearFrom,
      to: filters?.yearTo,
    },
    horsePower: {
      from: filters?.horsePowerFrom,
      to: filters?.horsePowerTo,
    },
    engineSize: {
      from: filters?.engineSizeFrom,
      to: filters?.engineSizeTo,
    },
    batteryCapacity: {
      from: filters?.batteryCapacityFrom,
      to: filters?.batteryCapacityTo,
    }
  }

  return res
}

export function formatProductionYears(years: string[]) {
  if (!years || years.length === 0) return null;
  const sorted = years.sort()
  return (sorted[0] ?? '') + " - " + (sorted[sorted.length - 1] ?? '')
}

export function createEmptyModelMatch(motorAtlasId: string | null | undefined): ModelMatch {
  return {
    eCode: motorAtlasId ?? "",
    batterySize: "",
    body: "",
    doors: "",
    drive: "",
    electricRangeMax: "",
    electricRangeMin: "",
    emission: "",
    engineSize: "",
    fuel: "",
    horsePower: "",
    make: "",
    marketDescription: "",
    model: "",
    productionYears: [],
    subModel: "",
    transmission: "",
    type: "",
    variant: "",
    recommendedPrice: undefined
  }
}
